import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { AlertTriangle, Check, RotateCcw, Calendar, Clock, User } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

export default function AdminNoShows() {
  const { user } = useAuth();
  const { toast } = useToast();
  const gymId = user?.gymId || "";
  const queryClient = useQueryClient();
  const { data: allSessions, isLoading, isError, error: queryError } = useQuery({
    queryKey: ["gym-sessions", gymId],
    queryFn: () => apiFetch(`/sessions/gym/${gymId}`),
    enabled: !!gymId,
  });
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  function refetch() { queryClient.invalidateQueries({ queryKey: ["gym-sessions", gymId] }); }

  const noShows = ((allSessions || []) as any[]).filter((s: any) => s.status === "NO_SHOW");

  async function resolveNoShow(sessionId: string, action: "refund" | "complete") {
    setActionLoading(sessionId);
    try {
      const endpoint = action === "refund" ? "no-show-refund" : "no-show-complete";
      await apiFetch(`/sessions/${sessionId}/${endpoint}`, { method: "PUT" });
      toast({ title: action === "refund" ? "No-show refunded" : "Marked as completed" });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">No-Show Resolution</h1>
          <p className="text-muted-foreground">Manage sessions flagged as no-shows</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Unresolved No-Shows</CardDescription>
              <CardTitle className="text-3xl font-mono text-red-400">{noShows.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Total Revenue at Risk</CardDescription>
              <CardTitle className="text-3xl font-mono text-amber-400">
                {formatCurrency(noShows.reduce((sum: number, s: any) => sum + (s.priceTotal || 0), 0))}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Trainer Payouts Pending</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">
                {formatCurrency(noShows.reduce((sum: number, s: any) => sum + (s.trainerPayout || 0), 0))}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {isError ? (
          <div className="p-12 text-center rounded-2xl border border-dashed border-red-500/20 bg-red-500/5">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-white font-medium text-lg">Failed to load sessions</p>
            <p className="text-sm text-muted-foreground mt-2">{(queryError as any)?.message || "An error occurred"}</p>
            <Button onClick={() => refetch()} variant="ghost" className="mt-4 text-primary"><RotateCcw className="w-4 h-4 mr-2" /> Retry</Button>
          </div>
        ) : isLoading ? (
          <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-32 bg-white/5 rounded-xl animate-pulse" />)}</div>
        ) : noShows.length === 0 ? (
          <div className="p-12 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
            <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <p className="text-white font-medium text-lg">All clear! No unresolved no-shows.</p>
            <p className="text-sm text-muted-foreground mt-2">Sessions flagged by the automated no-show detection will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {noShows.map((session: any) => (
              <Card key={session.id} className="glass-panel border-red-500/20">
                <CardContent className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                        <AlertTriangle className="w-6 h-6 text-red-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 font-semibold uppercase">No-Show</span>
                          <span className="text-xs text-muted-foreground font-mono">{session.id.slice(0, 8)}...</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <User className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-muted-foreground">Student:</span>
                            <span className="text-white font-medium">{session.student?.firstName} {session.student?.lastName}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <User className="w-3.5 h-3.5 text-primary" />
                            <span className="text-muted-foreground">Trainer:</span>
                            <span className="text-white font-medium">{session.trainer?.firstName} {session.trainer?.lastName}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-white">{format(new Date(session.proposedStart), "MMM d, yyyy")}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-white">{format(new Date(session.proposedStart), "h:mm a")} - {format(new Date(session.proposedEnd), "h:mm a")}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3">
                          <span className="text-sm text-white font-mono">Total: {formatCurrency(session.priceTotal || 0)}</span>
                          <span className="text-sm text-primary font-mono">Trainer payout: {formatCurrency(session.trainerPayout || 0)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 shrink-0 lg:min-w-[200px]">
                      <Button
                        size="sm"
                        disabled={actionLoading === session.id}
                        onClick={() => resolveNoShow(session.id, "complete")}
                        className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30 w-full"
                      >
                        <Check className="w-4 h-4 mr-2" /> Mark Completed
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={actionLoading === session.id}
                        onClick={() => resolveNoShow(session.id, "refund")}
                        className="text-amber-400 hover:bg-amber-500/10 w-full"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" /> Issue Refund
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
